import { ColumnDefinition } from "@/models/dashboard/WidgetConfig";

/**
 * Column visibility for the list widgets.
 *
 * A widget stores its choice as `visibleColumns: string[]`, where an empty
 * array means "whatever the columns declare as default" rather than "all of
 * them" — the reading the config form has always used. Both list widgets and
 * both config forms go through here so a column can never render in a widget
 * while its checkbox sits unticked.
 */
export const isWidgetColumnVisible = (
  column: ColumnDefinition,
  visibleColumns: string[]
): boolean =>
  visibleColumns.length === 0
    ? column.defaultVisible
    : visibleColumns.includes(column.id);

/** The columns to render, in the order the definitions declare. */
export const resolveVisibleWidgetColumns = (
  columns: ColumnDefinition[],
  visibleColumns: string[]
): ColumnDefinition[] =>
  columns.filter((column) => isWidgetColumnVisible(column, visibleColumns));

/**
 * Tick or untick one column, returning the new stored selection.
 *
 * Expanding an empty selection starts from the defaults, so unticking one
 * column cannot quietly reveal a column that was default-off.
 */
export const toggleWidgetColumn = (
  columns: ColumnDefinition[],
  visibleColumns: string[],
  columnId: string,
  checked: boolean
): string[] => {
  const selected = new Set(
    resolveVisibleWidgetColumns(columns, visibleColumns).map(
      (column) => column.id
    )
  );

  if (checked) {
    selected.add(columnId);
  } else {
    selected.delete(columnId);
  }

  return columns
    .filter((column) => selected.has(column.id))
    .map((column) => column.id);
};
